
import React, { useMemo, useState } from 'react';
import { Image, PenTool, FlaskConical, BookOpen } from 'lucide-react';
import { ParsedSunoOutput } from '@/types';
import PromptArchitectApp from './PromptArchitect/PromptArchitectApp';
import ExperimentalTab from './ImageConfig/ExperimentalTab';
import AtlasTab from './ImageConfig/AtlasTab';
import { formatSongDataAsMarkdown, formatSeedAsMarkdown } from './PromptArchitect/formatSongData';
import type { SongSeed } from './RiffWriter/types';


interface ImageConfigTabProps {
  output: ParsedSunoOutput | null;
  blueprint: string | null;
  onBlueprintGenerated: (b: string) => void;
  draftId?: string | null;
  /** Optional RiffWriter seed routed in via "Send to Art" — takes precedence over output. */
  artSeed?: SongSeed | null;
}

type ArtSubTab = 'architect' | 'experimental' | 'atlas';

const SUB_TABS: Array<{ id: ArtSubTab; label: string; Icon: typeof PenTool }> = [
  { id: 'architect', label: 'Prompt Architect', Icon: PenTool },
  { id: 'experimental', label: 'Experimental', Icon: FlaskConical },
  { id: 'atlas', label: 'Style Atlas', Icon: BookOpen },
];

export const ImageConfigTab: React.FC<ImageConfigTabProps> = ({ output, draftId, artSeed }) => {
  const [subTab, setSubTab] = useState<ArtSubTab>('architect');

  const masterInput = useMemo(() => {
    if (artSeed) return formatSeedAsMarkdown(artSeed);
    if (output) return formatSongDataAsMarkdown(output);
    return '';
  }, [artSeed, output]);

  if (!artSeed && !output) {
    return (
      <div className="min-h-[500px] flex flex-col items-center justify-center text-slate-400 rounded-xl p-12 text-center backdrop-blur-sm bg-slate-950/30">
        <Image size={48} className="mb-4 opacity-20" />
        <h3 className="text-lg font-medium text-slate-400">Album Art</h3>
        <p className="max-w-sm mt-2 text-sm">
          Generate your initial song on the 'Studio' tab first, or send a seed from RiffWriter.
          Then choose Prompt Architect, Experimental Concepts, or browse the Style Atlas.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sub-tab nav */}
      <div className="flex items-center gap-1 border-b border-slate-800">
        {SUB_TABS.map(({ id, label, Icon }) => {
          const active = subTab === id;
          return (
            <button
              key={id}
              onClick={() => setSubTab(id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-[11px] font-black uppercase tracking-widest border-b-2 transition-all ${
                active
                  ? 'border-accent text-accent'
                  : 'border-transparent text-slate-400 hover:text-slate-300'
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          );
        })}
      </div>

      {subTab === 'architect' && (
        <PromptArchitectApp initialMasterInput={masterInput} draftId={draftId ?? undefined} />
      )}
      {subTab === 'experimental' && <ExperimentalTab masterInput={masterInput} />}
      {subTab === 'atlas' && <AtlasTab songMarkdown={masterInput} />}
    </div>
  );
};
